import { useQueryClient } from "@tanstack/react-query";
import { createContext, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";

// Main Application Context
export const DashboardContext = createContext();

// Main Application Context Provider
export const DashboardContextProvider = ({ children }) => {
  // Redux state values & hooks
  const queryClient = useQueryClient();
  const isAdmin = useSelector((state) => state.user.admin);
  const user = useSelector((state) => state.user);
  const userId = useSelector((state) => state.user.id);
  const client = useSelector((state) => state.client);
  const clientId = useSelector((state) => state.user.currentClient);
  const projectId = useSelector((state) => state.user.currentProject);
  const archive = useSelector((state) => state.user.hideArchived);
  const navigate = useNavigate();

  const [visibleNav, setVisibleNav] = useState(true);

  // State values used across the dashboard
  const [heading, setHeading] = useState("Dashboard");
  const [hideArchived, setHideArchived] = useState(archive);
  const [openModal, setOpenModal] = useState(
    isAdmin === true ||
      (isAdmin === false && user.id !== "" && user.id !== null)
      ? false
      : true,
  );
  // Invite User
  const [emailAnchor, setEmailAnchor] = useState(null);
  const [openEmail, setOpenEmail] = useState(false);
  const [currEmailRow, setCurrEmailRow] = useState(null);
  // Contacts
  const [contactsAnchor, setContactsAnchor] = useState(null);
  const [openContacts, setOpenContacts] = useState(false);
  const [currContactsRow, setCurrContactsRow] = useState(null);
  // Services
  const [servicesAnchor, setServicesAnchor] = useState(null);
  const [openServices, setOpenServices] = useState(false);
  const [currServicesRow, setCurrServicesRow] = useState(null);
  const [swpppAnchor, setSwpppAnchor] = useState(null);
  const [openSwppp, setOpenSwppp] = useState(false);
  const [currSwpppRow, setCurrSwpppRow] = useState(null);
  const [dustAnchor, setDustAnchor] = useState(null);
  const [openDust, setOpenDust] = useState(false);
  const [currDustRow, setCurrDustRow] = useState(null);
  // Clients (Admin only) Select
  const [openSelect, setOpenSelect] = useState(false);
  // Sidebar
  const [openSidebar, setOpenSidebar] = useState(false);
  // Snackbar
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [snackbarMessage, setSnackbarMessage] = useState("");

  // Popover Handlers
  const openSwpppPopoverHandler = (e) => {
    let id = e.currentTarget.id;
    setCurrSwpppRow(id);
    setSwpppAnchor(e.currentTarget.parentElement);
  };

  const openDustPopoverHandler = (e) => {
    let id = e.currentTarget.id;
    setCurrDustRow(id);
    setDustAnchor(e.currentTarget.parentElement);
  };

  const openEmailPopoverHandler = (e) => {
    let id = e.currentTarget.id;
    setCurrEmailRow(id);
    setEmailAnchor(e.currentTarget.parentElement);
  };

  const openContactsPopoverHandler = (e) => {
    let id = e.currentTarget.id;
    setCurrContactsRow(id);
    setContactsAnchor(e.currentTarget.parentElement);
  };

  const openServicesPopoverHandler = (e) => {
    let id = e.currentTarget.id;
    setCurrServicesRow(id);
    setServicesAnchor(e.currentTarget.parentElement);
  };

  // Phone Number Input Formatter
  const handleFormatPhone = (value) => {
    if (!value) return value;

    const phoneNumber = value.replace(/[\D]/g, "");

    const phoneNumberLength = phoneNumber.length;

    if (phoneNumberLength < 4) return phoneNumber;

    if (phoneNumberLength < 7) {
      return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`;
    }

    return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(
      3,
      6,
    )}-${phoneNumber.slice(6, 10)}`;
  };

  const formatFaxId = (value) => {
    if (!value) return value;
    // Remove any non-numeric characters
    const faxNumber = value.replace(/\D/g, "");
    const faxLength = faxNumber.length;

    // Format as xxx-xxx-xxxx
    if (faxLength < 4) return faxNumber;

    if (faxLength < 7) return `${faxNumber.slice(0, 3)}-${faxNumber.slice(3)}`;

    return `${faxNumber.slice(0, 3)}-${faxNumber.slice(
      3,
      6,
    )}-${faxNumber.slice(6, 10)}`;
  };

  function formatParcelNumber(value) {
    if (!value) return value;

    // Remove any non-numeric characters
    let cleanValue = value.replace(/\D/g, "");

    const cleanValueLength = cleanValue.length;

    // Format as xxx-xx-xxx-xxx
    if (cleanValueLength <= 3) return cleanValue;

    if (cleanValueLength <= 5)
      return `${cleanValue.slice(0, 3)}-${cleanValue.slice(3)}`;

    if (cleanValueLength <= 8)
      return `${cleanValue.slice(0, 3)}-${cleanValue.slice(3, 5)}-${cleanValue.slice(5)}`;

    return `${cleanValue.slice(0, 3)}-${cleanValue.slice(3, 5)}-${cleanValue.slice(5, 8)}-${cleanValue.slice(8, 11)}`;
  }

  function formatTaxId(value) {
    if (!value) return value;

    // Remove any non-numeric characters
    const cleanValue = value.replace(/\D/g, "");
    const cleanValueLength = cleanValue.length;

    // Format as xx-xxxxxxx
    if (cleanValueLength <= 2) return cleanValue;

    return `${cleanValue.slice(0, 2)}-${cleanValue.slice(2, 9)}`;
  }

  // Person Names Input Formatter
  const capitalizeNameAfterSpace = (value) => {
    return value
      .toLowerCase()
      .split(" ")
      .map(
        (word) =>
          word.charAt(0).toUpperCase() + word.substring(1).toLowerCase(),
      )
      .join(" ");
  };

  return (
    <DashboardContext.Provider
      value={{
        client,
        clientId,
        projectId,
        user,
        userId,
        isAdmin,
        visibleNav,
        setVisibleNav,
        heading,
        setHeading,
        hideArchived,
        setHideArchived,
        openModal,
        setOpenModal,
        navigate,
        queryClient,
        servicesAnchor,
        setServicesAnchor,
        openServices,
        setOpenServices,
        currServicesRow,
        setCurrServicesRow,
        openServicesPopoverHandler,
        swpppAnchor,
        setSwpppAnchor,
        openSwppp,
        setOpenSwppp,
        currSwpppRow,
        setCurrSwpppRow,
        openSwpppPopoverHandler,
        dustAnchor,
        setDustAnchor,
        openDust,
        setOpenDust,
        currDustRow,
        setCurrDustRow,
        openDustPopoverHandler,
        openSelect,
        setOpenSelect,
        openSidebar,
        setOpenSidebar,
        handleFormatPhone,
        formatFaxId,
        formatParcelNumber,
        formatTaxId,
        capitalizeNameAfterSpace,
        openEmail,
        setOpenEmail,
        emailAnchor,
        setEmailAnchor,
        currEmailRow,
        setCurrEmailRow,
        openEmailPopoverHandler,
        openSnackbar,
        setOpenSnackbar,
        snackbarSeverity,
        setSnackbarSeverity,
        snackbarMessage,
        setSnackbarMessage,
        contactsAnchor,
        setContactsAnchor,
        openContacts,
        setOpenContacts,
        currContactsRow,
        setCurrContactsRow,
        openContactsPopoverHandler,
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
};
